/**
 * Auth provider (DEC-141). Owns the `/auth/me` lifecycle; the context and hooks
 * it feeds live in `useAuth.ts`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchMe, logout as apiLogout } from '../api/auth'
import type { AuthClaims } from '../api/client'
import { AuthContext } from './useAuth'
import type { AuthContextValue, AuthStatus } from './useAuth'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('resolving')
  const [claims, setClaims] = useState<AuthClaims | null>(null)

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe()
      setClaims(me)
      setStatus('authenticated')
    } catch {
      // A 401 here is the ordinary answer for a signed-out visitor, not an
      // error. `client.ts` excludes /auth/me from its global redirect for the
      // same reason: redirecting on it would loop the login page with itself.
      setClaims(null)
      setStatus('anonymous')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    try {
      await apiLogout()
    } finally {
      // Hard navigation, not React Router. A soft navigate would leave the React
      // Query cache, the Zustand scan store and any live EventSource alive in
      // memory; this tears the whole tree down before re-entry. The rule
      // predates DEC-141 (UI_SDD §5) and still holds. What changed is that the
      // server call above now actually ends the session.
      window.location.href = '/login'
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, claims, refresh, signOut }),
    [status, claims, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
